<template>
  <div class="card custom-shadow rounded-3 bg-white border mb-4">
    <div class="card-body p-4">
      <div
        class="d-flex justify-content-between align-items-center flex-wrap gap-1 mb-3"
      >
        <h4 class="mb-0 fw-semibold" v-html="t('fr.deadlines.widget_title', 'Dettaglio Scadenze')"></h4>
        <p class="mb-0"><small v-html="t('fr.deadlines.widget_description', 'Tutte le scadenze che non dovrai più temere.')"></small></p>
      </div>

      <!-- Year Selection Tabs -->
      <ul
        class="nav nav-tabs bg-transparent border-bottom gap-2 gap-sm-5 pb-4"
        id="yearDeadlinesTab"
        role="tablist"
      >
        <li class="nav-item" role="presentation">
          <button
            :class="['nav-link', 'p-0', 'bg-transparent', 'border-0', 'fs-12', 'fw-medium', 'text-body', { 'active': activeYearTab === 'current' }]"
            @click="activeYearTab = 'current'"
            type="button"
            role="tab"
            aria-selected="true"
          >
            <span v-html="t('fr.deadlines.current_year_tab', `Scadenze ${currentYear}`, { currentYear })"></span>
          </button>
        </li>
        <li class="nav-item" role="presentation">
          <button
            :class="['nav-link', 'p-0', 'bg-transparent', 'border-0', 'fs-12', 'fw-medium', 'text-body', { 'active': activeYearTab === 'predictions' }]"
            @click="activeYearTab = 'predictions'"
            type="button"
            role="tab"
            aria-selected="false"
          >
            <span v-html="t('fr.deadlines.next_year_tab', `Scadenze ${nextYear}`, { nextYear })"></span>
          </button>
        </li>
      </ul>

      <!-- Deadlines Table -->
      <div class="tab-content" id="deadlinesTabContent">
        <div
          class="tab-pane fade show active"
          id="deadlines-table-pane"
          role="tabpanel"
          aria-labelledby="deadlines-table-tab"
          tabindex="0"
        >
          <div class="default-table-area style-two campaigns-table">
            <div class="table-responsive">
              <table class="table align-middle border-0">
                <tbody>
                  <tr v-for="deadline in paginatedDeadlines" :key="deadline.paymentType">
                    <!-- Column 1: Description and Date with Border -->
                    <td>
                      <div
                        :class="['border-start', 'border-2', 'ps-3', 'py-0', getCategoryColor(deadline.category)]"
                      >
                        <h6 class="fs-14 fw-semibold mb-0"><small>{{ deadline.description }}</small> &nbsp;
                          <small
                        :class="['d-inline-block', 'py-1', 'px-2', 'rounded-2']"
                        :style="{ 'background-color': hexToRgba(getCategoryHexColor(deadline.category), 0.1), 'color': getCategoryHexColor(deadline.category) }"
                      >
                        <span v-html="getCategoryLabel(deadline.category)"></span>
                      </small>
                        </h6>
                        <span class="fs-12">{{ formatDate(deadline.date) }}</span>
                      </div>
                    </td>

                    <td style="text-align: right;">
                       <span class="fs-14 fw-semibold">{{ formatCurrency(deadline.amount) }}</span>
                    </td>
                  </tr>
                  <tr v-if="selectedDeadlinesByYear.length === 0">
                    <td colspan="2" class="text-center" v-html="t('fr.deadlines.no_deadlines_message', 'Nessuna scadenza trovata per questa categoria.')"></td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- Items per page and Pagination Controls -->
          <div class="d-flex justify-content-between align-items-center mt-3">
            <!-- Items per page selector -->
            <div class="d-flex align-items-center">
              <label class="me-2 fw-medium fs-14">Righe per pagina:</label>
              <select
                v-model="itemsPerPage"
                @change="onItemsPerPageChange"
                class="form-select form-select-sm"
                style="width: auto;"
              >
                <option :value="5">5</option>
                <option :value="10">10</option>
                <option :value="20">20</option>
                <option :value="30">30</option>
              </select>
            </div>

            <!-- Pagination Controls -->
            <nav v-if="totalPages > 1" aria-label="Page navigation example">
              <ul class="pagination justify-content-center mb-0">
                <li class="page-item" :class="{ 'disabled': currentPage === 1 }">
                  <a class="page-link" href="#" @click.prevent="prevPage"><i class="ri-arrow-left-line"></i></a>
                </li>
                <li class="page-item" v-for="page in totalPages" :key="page" :class="{ 'active': page === currentPage }">
                  <a class="page-link" href="#" @click.prevent="goToPage(page)">{{ page }}</a>
                </li>
                <li class="page-item" :class="{ 'disabled': currentPage === totalPages }">
                  <a class="page-link" href="#" @click.prevent="nextPage"><i class="ri-arrow-right-line"></i></a>
                </li>
              </ul>
            </nav>
          </div>

        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { defineProps, ref, computed, watch } from 'vue';
import { t } from '@/utils/i18nHelper';

const props = defineProps({
  results: {
    type: Object,
    required: true,
  },
});

const currentYear = new Date().getFullYear();
const nextYear = currentYear + 1;

const activeYearTab = ref('current'); // 'current' or 'predictions'

const currentPage = ref(1);
const itemsPerPage = ref(5); // Default 5 items per page

const totalPages = computed(() => {
  return Math.ceil(selectedDeadlinesByYear.value.length / itemsPerPage.value);
});

const paginatedDeadlines = computed(() => {
  const start = (currentPage.value - 1) * itemsPerPage.value;
  const end = start + itemsPerPage.value;
  return selectedDeadlinesByYear.value.slice(start, end);
});

watch(activeYearTab, () => {
  currentPage.value = 1; // Reset current page when year tab changes
});

const selectedDeadlinesByYear = computed(() => {
  let deadlines = [];
  if (activeYearTab.value === 'current') {
    deadlines = props.results?.current?.deadlines || [];
  } else {
    deadlines = props.results?.predictions?.deadlines || [];
  }
  // Sort the deadlines by date in ascending order
  return [...deadlines].sort((a, b) => {
    return new Date(a.date).getTime() - new Date(b.date).getTime();
  });
});
const getCategoryColor = (category: string) => {
  switch (category) {
    case 'IRES':
      return 'border-tax';
    case 'INPS':
      return 'border-inps-variable';
    case 'INPS_FISSI':
      return 'border-inps-fixed';
    // Casse Professionali
    case 'SOGGETTIVO':
      return 'border-contrib-soggettivo';
    case 'INTEGRATIVO':
      return 'border-contrib-integrativo';
    case 'MATERNITA':
      return 'border-contrib-maternita';
    default:
      return ''; // No specific border color class
  }
};

const getCategoryHexColor = (category: string) => {
  switch (category) {
    case 'IRES':
      return '#dc3545'; // Rosso - coerente con TaxBreakdownChart
    case 'INPS':
      return '#fd7e14'; // Arancione - coerente con TaxBreakdownChart
    case 'INPS_FISSI':
      return '#ffc107'; // Giallo - coerente con TaxBreakdownChart
    // Casse Professionali (usano gli stessi colori dei contributi INPS)
    case 'SOGGETTIVO':
      return '#fd7e14'; // Arancione (come INPS Variable)
    case 'INTEGRATIVO':
      return '#ffc107'; // Giallo (come INPS Fixed)
    case 'MATERNITA':
      return '#9c27b0'; // Viola (specifico per maternità)
    default:
      return '#cccccc'; // Default color
  }
};

const getCategoryLabel = (category: string) => {
  const keyMap = {
    'IRES': 'fr.deadlines.category_taxes',
    'INPS': 'fr.deadlines.category_inps_variable',
    'INPS_FISSI': 'fr.deadlines.category_inps_fixed',
    // Casse Professionali
    'SOGGETTIVO': 'fr.deadlines.category_contrib_soggettivo',
    'INTEGRATIVO': 'fr.deadlines.category_contrib_integrativo',
    'MATERNITA': 'fr.deadlines.category_contrib_maternita'
  };

  const defaultLabels = {
    'IRES': 'Imposte',
    'INPS': 'INPS Variabili',
    'INPS_FISSI': 'INPS Fissi',
    // Casse Professionali
    'SOGGETTIVO': 'Contrib. Soggettivo',
    'INTEGRATIVO': 'Contrib. Integrativo',
    'MATERNITA': 'Contrib. Maternità'
  };

  const key = keyMap[category as keyof typeof keyMap];
  const defaultLabel = defaultLabels[category as keyof typeof defaultLabels] || category;

  return key ? t(key, defaultLabel) : defaultLabel;
};

const hexToRgba = (hex: string, alpha: number) => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const formatDate = (dateString: string) => {
  const options: Intl.DateTimeFormatOptions = { year: 'numeric', month: 'long', day: 'numeric' };
  return new Date(dateString).toLocaleDateString('it-IT', options);
};

const formatCurrency = (amount: number) => {
  const numVal = parseFloat(amount.toString());
  if (isNaN(numVal)) return '€ 0,00';
  const formatted = numVal.toFixed(2);
  const parts = formatted.split('.');
  const integerPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  const decimalPart = parts[1];
  return '€ ' + integerPart + ',' + decimalPart;
};

const prevPage = () => {
  if (currentPage.value > 1) {
    currentPage.value--;
  }
};

const nextPage = () => {
  if (currentPage.value < totalPages.value) {
    currentPage.value++;
  }
};

const goToPage = (page: number) => {
  currentPage.value = page;
};

const onItemsPerPageChange = () => {
  currentPage.value = 1; // Reset to first page when changing items per page
};

</script>

<style scoped>
/* Colori coerenti con TaxBreakdownChart */
.border-tax {
  border-color: #dc3545 !important; /* Rosso - Imposta Sostitutiva */
}
.border-inps-variable {
  border-color: #fd7e14 !important; /* Arancione - INPS Variabili */
}
.border-inps-fixed {
  border-color: #ffc107 !important; /* Giallo - INPS Fissi */
}

/* Casse Professionali */
.border-contrib-soggettivo {
  border-color: #fd7e14 !important; /* Arancione - Contributo Soggettivo */
}
.border-contrib-integrativo {
  border-color: #ffc107 !important; /* Giallo - Contributo Integrativo */
}
.border-contrib-maternita {
  border-color: #9c27b0 !important; /* Viola - Contributo Maternità */
}

/* Tab styles for better visibility */
.nav-tabs .nav-link {
  color: #6c757d;
  border: none;
  border-bottom: 2px solid transparent;
  transition: all 0.3s ease;
}

.nav-tabs .nav-link:hover {
  color: #495057;
  border-bottom-color: #dee2e6;
}

.nav-tabs .nav-link.active {
  color: #304BF7 !important;
  border-bottom-color: #304BF7 !important;
  font-weight: 600 !important;
  background-color: transparent;
}

.nav-tabs .nav-link.active span {
  color: #304BF7;
}
</style>